import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { fetchMyReferrals } from "../lib/referrals";
import { useSettings } from "../lib/settings";
import { Copy, Check, Share2, TrendingUp, Users, Wallet, Loader2, Crown, Sparkles, Download, Lock, Star, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import PartnerAchievements from "../components/PartnerAchievements";
import CompassionScore from "../components/CompassionScore";
import QRCode from "../components/QRCode";
const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, accent = "#D4AF37" }) => (
  <div className="border border-white/10 rounded-2xl p-6 bg-white/[0.02]">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] tracking-[0.25em] uppercase text-white/45">{label}</span>
      <Icon size={16} style={{ color: accent }} />
    </div>
    <div className="serif text-4xl font-light" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-xs text-white/45 mt-1">{sub}</div>}
  </div>
);

const TIER_ICONS = {
  ambassador: Star,
  mentor_partner: Sparkles,
  master_partner: Crown,
};
const TIER_COLORS = {
  ambassador: "#a48cff",
  mentor_partner: "#D4AF37",
  master_partner: "#F2D6D6",
};
const TIER_ORDER = ["ambassador", "mentor_partner", "master_partner"];

const ReferralsPage = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const settings = useSettings();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [spot, setSpot] = useState(null);
  const [spotSaving, setSpotSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth/login?redirect=/referrals"); return; }
    fetchMyReferrals().then(setData);
    const token = localStorage.getItem("sat_token");
    axios.get(`${API}/api/partners/me/spotlight`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSpot(r.data)).catch(() => {});
  }, [user, loading, nav]);

  if (loading || !user) return <div className="pt-32 text-center text-white/55"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!data) return <div className="pt-32 text-center text-white/55">Lade…</div>;

  const link = `${window.location.origin}/?ref=${data.referral_code}`;
  const tiers = data.all_tiers || {};
  const myTierKey = data.tier?.key || "ambassador";

  const saveSpot = async (patch) => {
    setSpotSaving(true);
    try {
      const token = localStorage.getItem("sat_token");
      const r = await axios.patch(`${API}/api/partners/me/spotlight`, patch, { headers: { Authorization: `Bearer ${token}` } });
      setSpot(r.data);
    } finally { setSpotSaving(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "SAT — Die Spielregeln des Lebens", text: "Ich glaube, das könnte dich interessieren.", url: link }); } catch (e) { /* user cancelled */ }
    } else copy();
  };

  return (
    <div className="pt-28 min-h-screen bg-[#081328]" data-testid="referrals-page">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10">
        {/* Hero */}
        <div className="mb-12">
          <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-4">SAT Partner Programm</div>
          <h1 className="serif text-5xl md:text-6xl font-light tracking-tight leading-[1.05]">
            Du glaubst an die Mission.<br/>
            <span className="italic" style={{ color: "#D4AF37" }}>Wir glauben an dich.</span>
          </h1>
          <p className="text-white/65 text-base md:text-lg mt-6 max-w-3xl leading-relaxed">
            Empfehle SAT an Menschen, denen es helfen würde — unterstütze die Transformation anderer und profitiere gleichzeitig von attraktiven Provisionen. Dein Weg vom Ambassador zum Master Partner ist klar strukturiert und fair vergütet.
          </p>
        </div>

        {/* Current tier banner */}
        <div className="mb-10 rounded-3xl p-8 border-2 relative overflow-hidden"
             style={{ borderColor: TIER_COLORS[myTierKey], background: `radial-gradient(ellipse at top right, ${TIER_COLORS[myTierKey]}25 0%, transparent 60%), #0E1A30` }}
             data-testid="current-tier-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {(() => { const I = TIER_ICONS[myTierKey] || Star; return <I size={36} style={{ color: TIER_COLORS[myTierKey] }} />; })()}
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1">Deine aktuelle Stufe</div>
                <div className="serif text-3xl font-light" style={{ color: TIER_COLORS[myTierKey] }}>{data.tier?.label}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Deine Provision</div>
              <div className="serif text-5xl font-light" style={{ color: TIER_COLORS[myTierKey] }}>{(data.commission_rate * 100).toFixed(0)}%</div>
            </div>
          </div>
        </div>

        {/* Referral link card */}
        <div className="mb-10 border border-white/10 rounded-3xl p-6 md:p-8 bg-white/[0.02]" data-testid="referral-link-card">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-white/45 mb-2">Dein persönlicher Empfehlungs-Link</div>
              <div className="font-mono text-sm text-white/85 truncate" data-testid="referral-link-text">{link}</div>
              <div className="text-xs text-white/45 mt-2">Code: <span className="text-[#D4AF37] font-medium">{data.referral_code}</span> · 30 Tage Cookie-Attribution</div>
              <div className="flex gap-2 mt-4">
                <button onClick={copy} className="px-4 py-2.5 border border-white/15 hover:border-[#D4AF37] rounded-full text-sm inline-flex items-center gap-2 transition" data-testid="referral-copy">
                  {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Kopiert" : "Kopieren"}
                </button>
                <button onClick={share} className="px-4 py-2.5 bg-[#D4AF37] text-[#081328] font-semibold rounded-full text-sm inline-flex items-center gap-2 hover:bg-[#e3c252] transition" data-testid="referral-share">
                  <Share2 size={14} /> Teilen
                </button>
              </div>
            </div>
            <div className="shrink-0">
              <QRCode value={link} size={6} label="Scannen zum Teilen" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="referral-stats">
          <StatCard icon={Users} label="Besuche" value={data.stats.visits} />
          <StatCard icon={TrendingUp} label="Bezahlte Orders" value={data.stats.paid_orders} accent="#a48cff" />
          <StatCard icon={Wallet} label="Verdient CHF" value={Number(data.stats.total_earned_chf ?? 0).toFixed(2)} sub={`${Number(data.stats.pending_chf ?? 0).toFixed(2)} ausstehend`} />
          <StatCard icon={Wallet} label="Ausbezahlt CHF" value={Number(data.stats.paid_out_chf ?? 0).toFixed(2)} accent="#48d597" />
        </div>

        {/* Compassion Score — bridges SAT & Herzbewegung */}
        <CompassionScore variant="sat" />

        {/* Achievements */}
        <PartnerAchievements />

        {/* Tier comparison */}
        <div className="mb-12">
          <div className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3">Die drei Stufen</div>
          <h2 className="serif text-3xl md:text-4xl font-light mb-8">Wachse mit der Bewegung.</h2>
          <div className="grid md:grid-cols-3 gap-5" data-testid="tier-comparison">
            {TIER_ORDER.map((key) => {
              const tier = tiers[key];
              if (!tier) return null;
              const Icon = TIER_ICONS[key];
              const isMine = key === myTierKey;
              return (
                <div key={key}
                     className={`relative rounded-2xl p-7 border-2 transition ${isMine ? "scale-[1.02]" : "border-white/10 bg-white/[0.02]"}`}
                     style={isMine ? { borderColor: TIER_COLORS[key], background: `linear-gradient(180deg, ${TIER_COLORS[key]}15 0%, transparent 70%)` } : {}}
                     data-testid={`tier-${key}`}>
                  {isMine && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] tracking-widest uppercase rounded-full"
                         style={{ background: TIER_COLORS[key], color: "#081328" }}>Deine Stufe</div>
                  )}
                  <Icon size={28} style={{ color: TIER_COLORS[key] }} />
                  <h3 className="serif text-2xl font-light mt-4" style={{ color: TIER_COLORS[key] }}>{tier.label}</h3>
                  <div className="serif text-5xl font-light mt-2" style={{ color: TIER_COLORS[key] }}>
                    {(tier.rate * 100).toFixed(0)}<span className="text-2xl">%</span>
                  </div>
                  <div className="text-[10px] tracking-widest uppercase text-white/45 mt-1">Provision</div>
                  <ul className="mt-6 space-y-2 text-sm text-white/70">
                    {tier.perks.map((p, i) => (
                      <li key={i} className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0" style={{ color: TIER_COLORS[key] }} /> {p}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* Marketing Materials Download */}
        {settings?.marketing_materials && (
          <div className="mb-12">
            <div className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3">Werbematerialien</div>
            <h2 className="serif text-3xl md:text-4xl font-light mb-6">Alles, was du zum Teilen brauchst.</h2>
            <div className="grid md:grid-cols-2 gap-3" data-testid="marketing-materials">
              {settings.marketing_materials.map((m) => (
                <div key={m.id} className="flex items-center justify-between p-5 border border-white/10 rounded-xl bg-white/[0.02]"
                     data-testid={`material-${m.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5">
                      {m.available ? <Download size={18} className="text-[#D4AF37]" /> : <Lock size={16} className="text-white/35" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white/90">{m.label}</div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40">{m.type}</div>
                    </div>
                  </div>
                  {m.available && m.url ? (
                    <a href={m.url} download className="px-4 py-2 text-xs uppercase tracking-wider border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#081328] rounded-full transition">
                      Download
                    </a>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-white/40">In Kürze</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Partner Spotlight Opt-In */}
        {spot && (
          <div className="mb-12 border border-white/10 rounded-2xl p-6 md:p-8 bg-white/[0.02]" data-testid="spotlight-optin-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4">
                {spot.opt_in ? <Eye size={20} className="text-[#48d597] mt-1" /> : <EyeOff size={20} className="text-white/45 mt-1" />}
                <div>
                  <div className="serif text-xl">Partner-Spotlight</div>
                  <div className="text-white/55 text-sm mt-1 max-w-xl">
                    Möchtest du als SAT Partner in der Bewegung sichtbar werden? Dein Name & deine Stufe können dann gelegentlich im Footer oder auf der Startseite erscheinen — das motiviert andere und gibt deiner Empfehlungsarbeit ein Gesicht.
                  </div>
                </div>
              </div>
              <button onClick={() => saveSpot({ opt_in: !spot.opt_in })} disabled={spotSaving}
                      className={`px-5 py-2.5 rounded-full text-sm font-semibold transition disabled:opacity-50 ${
                        spot.opt_in ? "bg-[#48d597]/20 text-[#48d597] border border-[#48d597]/40 hover:bg-[#48d597]/30" : "bg-[#D4AF37] text-[#081328] hover:bg-[#e3c252]"
                      }`}
                      data-testid="spotlight-toggle">
                {spot.opt_in ? "Sichtbar — ausschalten" : "Im Spotlight sichtbar machen"}
              </button>
            </div>

            {spot.opt_in && (
              <div className="mt-4 mb-2">
                <Link to="/partner-wall" className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:underline" data-testid="link-to-wall">
                  → Du bist auf der <b>Wall of Partners</b> sichtbar. Hier ansehen.
                </Link>
              </div>
            )}
            {spot.opt_in && (
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/45">Anzeigename (optional)</label>
                  <input type="text" defaultValue={spot.spotlight_name}
                    onBlur={(e) => e.target.value !== spot.spotlight_name && saveSpot({ spotlight_name: e.target.value })}
                    placeholder={user?.name || "z. B. Marina K."}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                    data-testid="spotlight-name-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/45">Avatar-URL (optional)</label>
                  <input type="url" defaultValue={spot.spotlight_avatar_url}
                    onBlur={(e) => e.target.value !== spot.spotlight_avatar_url && saveSpot({ spotlight_avatar_url: e.target.value })}
                    placeholder="https://…"
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                    data-testid="spotlight-avatar-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-white/45">Dein Zitat (max. 240 Zeichen)</label>
                  <textarea defaultValue={spot.spotlight_quote} rows={2} maxLength={240}
                    onBlur={(e) => e.target.value !== spot.spotlight_quote && saveSpot({ spotlight_quote: e.target.value })}
                    placeholder="Was SAT in meinem Leben bewegt hat…"
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                    data-testid="spotlight-quote-input" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Commissions table */}
        <div className="mb-12">
          <h2 className="serif text-2xl mb-4">Deine Provisionen</h2>
          <div className="border border-white/10 rounded-2xl overflow-hidden" data-testid="referral-commissions">
            {data.commissions.length === 0 ? (
              <div className="p-8 text-center text-white/45">Noch keine Provisionen — beginne, deinen Link zu teilen.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.03] text-xs uppercase tracking-widest text-white/45">
                    <tr>
                      <th className="text-left px-5 py-4">Datum</th>
                      <th className="text-left px-5 py-4">Produkt</th>
                      <th className="text-left px-5 py-4">Käufer</th>
                      <th className="text-right px-5 py-4">Order CHF</th>
                      <th className="text-right px-5 py-4">Provision</th>
                      <th className="text-left px-5 py-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.commissions.map((c) => (
                      <tr key={c.id} className="border-t border-white/5">
                        <td className="px-5 py-3 text-white/65 text-xs">{c.created_at?.slice(0, 10)}</td>
                        <td className="px-5 py-3 text-white/85">{c.product_key}</td>
                        <td className="px-5 py-3 text-white/60 text-xs">{c.buyer_email}</td>
                        <td className="px-5 py-3 text-right text-white/75">{Number(c.order_amount ?? 0).toFixed(2)}</td>
                        <td className="px-5 py-3 text-right text-[#D4AF37] font-medium">{Number(c.amount ?? 0).toFixed(2)}</td>
                        <td className="px-5 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${c.status === "paid" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-white/5 text-white/60"}`}>
                            {c.status === "paid" ? "ausbezahlt" : "ausstehend"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="text-sm text-white/50">
          <Link to="/academy/dashboard" className="hover:text-white">← Mein Bereich</Link>
        </div>
      </div>
    </div>
  );
};

export default ReferralsPage;
